import type { DocumentBox } from "@volca/ui/components/ui/document-viewer";
import type { DocumentView } from "./views";

/**
 * A place in a document a figure came from, as the Documents tab reads it
 * from its URL. Every part is optional; a viewer uses the parts its kind
 * has:
 *
 * - PDF: `page` (1-based) and `box` (fractions of that page) are drawn and
 *   scrolled to; `find` is searched for and its matches highlighted.
 * - Spreadsheet (XLSX, and a CSV, drawn as one): `sheet` (name or 0-based
 *   index; a CSV has one), `row` (1-based, as in the sheet's row gutter) and
 *   `column` (letters, or a 1-based number) outline a cell and its row.
 * - Preview (the tables read from a file no viewer draws): `sheet` (0-based
 *   table index), `row` (the row's number, the "#" column) and `column`
 *   (the column's name, or a 1-based number) outline a cell.
 */
export interface DocumentCitation {
  page?: number;
  box?: DocumentBox;
  sheet?: string;
  row?: number;
  column?: string;
  find?: string;
}

/** The URL parameters a citation is written to, besides `doc` and `view`. */
export const CITATION_PARAMS = [
  "page",
  "box",
  "sheet",
  "row",
  "col",
  "find",
] as const;

const positiveInt = (value: string | null) => {
  const number = Number(value);
  return value && Number.isInteger(number) && number > 0 ? number : undefined;
};

/** "0.1,0.2,0.3,0.05" → a box; anything else (or outside 0–1) → undefined. */
export const parseBox = (value: string | null): DocumentBox | undefined => {
  const parts = value?.split(",").map(Number);
  if (
    parts?.length !== 4 ||
    parts.some((part) => !Number.isFinite(part) || part < 0 || part > 1)
  )
    return undefined;
  const [left, top, width, height] = parts;
  return { left, top, width, height };
};

/** The citation in `?page=&box=&sheet=&row=&col=&find=`; null when none. */
export const citationFromParams = (
  params: URLSearchParams,
): DocumentCitation | null => {
  const citation: DocumentCitation = {
    page: positiveInt(params.get("page")),
    box: parseBox(params.get("box")),
    sheet: params.get("sheet") || undefined,
    row: positiveInt(params.get("row")),
    column: params.get("col") || undefined,
    find: params.get("find")?.trim() || undefined,
  };
  return Object.values(citation).some((value) => value !== undefined)
    ? citation
    : null;
};

export const citationParams = (citation: DocumentCitation) => {
  const params: Record<string, string> = {};
  if (citation.page) params.page = String(citation.page);
  if (citation.box)
    params.box = [
      citation.box.left,
      citation.box.top,
      citation.box.width,
      citation.box.height,
    ]
      .map((value) => Number(value.toFixed(4)))
      .join(",");
  if (citation.sheet !== undefined) params.sheet = citation.sheet;
  if (citation.row) params.row = String(citation.row);
  if (citation.column !== undefined) params.col = citation.column;
  if (citation.find) params.find = citation.find;
  return params;
};

/**
 * The Documents tab's search for a document opened at a citation:
 * `doc=…&view=…&page=…`. The page hosting the tab adds its own parameters
 * (a template page keeps `?period=`).
 */
export const citationSearch = (
  documentId: string,
  citation?: DocumentCitation | null,
  view: DocumentView = "original",
) =>
  new URLSearchParams({
    doc: documentId,
    view,
    ...citationParams(citation ?? {}),
  });
